<template>
  <Modal
    ref="deleteProductModalRef"
    @onNegativeActionClick="handleDismissModal"
    @modalClosed="handleModalClosed"
    @onPositiveActionClick="handleConfirmDelete"
    :isPositiveActionPending="isDeleteLoading"
    :isPositiveActionButtonDisabled="isDeleteLoading"
    negativeActionText="Vazgeç"
    :positiveActionText="positiveActionText"
    :title="modalTitle"
    class="delete-modal"
  >
    <template #body>
      <!-- ürün detay -->
      <template v-if="pageName === 'detail'">
        <p>Ürün ile birlikte aşağıdaki bilgiler de silinecektir.</p>
        <ul class="delete-modal-list">
          <li>Geçmiş kampanya bilgileri</li>
          <li>Tüm varyantlar</li>
          <li>Ürün bazlı iade adresi</li>
          <li>Kullanıcı yorumları</li>
          <li>Ürün açıklamaları</li>
        </ul>
      </template>

      <!-- ürün listesi -->
      <template v-else>
        <template v-if="!productStatus.includes('InApproval')">
          <p>Ürünler ile birlikte aşağıdaki bilgiler de silinecektir:</p>
          <ul class="delete-modal-list">
            <li><b>Ürünlerin geçmiş kampanya bilgileri</b></li>
            <li><b>Ürün bazlı iade adresi</b></li>
          </ul>
          <p class="delete-modal-bottomText">
            (Ürün bazlı iade adresi, ürünlerle birlikte silineceği için iadeler
            ana adresinize yapılacaktır.)
          </p>
        </template>
        <template v-else>
          <p class="modal-text">
            Seçili ürünler silinecektir.<br />
            Ürünleri doğru bilgilerle tekrar katalog onayına <br />
            gönderebilirsiniz.
          </p>
        </template>
      </template>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { Modal } from "@n11/so-ui-components";
import {
  sendProductsDeletedEvent,
  sendProductDetailEvent,
} from "@/utils/ga4/helper";
import { GA4_EVENT } from '@/utils/ga4/event';
import { ToastMessageTypes } from "@/utils/enum/toastMessageTypes";

const props = defineProps({
  pageName: {
    type: String,
    required: true,
    validator: (value) => ["detail", "listing"].includes(value),
  },
  productId: {
    type: [String, Number],
    required: false,
  },
  selectedProducts: {
    type: [Object, Array],
    required: false,
    default: () => ({}),
  },
  productStatus: {
    type: Array,
    required: false,
    default: () => [],
  },
  tabParam: {
    type: String,
    required: false,
    default: "",
  },
});

const emit = defineEmits(["successDeleteProduct"]);

const store = useStore();
const router = useRouter();
const deleteProductModalRef = ref();
const isDeleteLoading = ref(false);
const closedByUsRef = ref(false);

const positiveActionText = computed(() =>
  props.pageName === "detail" ? "Ürünü Sil" : "Ürünleri Sil"
);

const modalTitle = computed(() => {
  if (props.pageName === "detail") {
    return "Ürünü silmek istediğinize emin misiniz?";
  }
  return props.productStatus.includes("InApproval")
    ? "Ürünleri silmek istediğinize emin misiniz?"
    : "Seçili ürünleri silmek istediğinize emin misiniz?";
});

const getProductIds = () => {
  if (props.pageName === "detail") {
    return [props.productId];
  }
  
  return Object.keys(props.selectedProducts);
};

const deleteMessageType = (data) => {
  const hasFailed = data?.failProductIds?.length > 0;
  const hasSucceeded = data?.successProductIds?.length > 0;

  if (hasFailed && hasSucceeded) {
    return ToastMessageTypes.WARNING;
  }
  if (hasFailed) {
    return ToastMessageTypes.ERROR;
  }
  if (hasSucceeded) {
    return ToastMessageTypes.SUCCESS;
  }
  return props.pageName === "detail" ? ToastMessageTypes.SUCCESS : false;
};

const openDeleteModal = () => deleteProductModalRef?.value?.openModal();
const closeDeleteModal = () => deleteProductModalRef?.value?.hiddenModal();

const handleDismissModal = () => {
  if (props.pageName === "detail") {
    sendProductDetailEvent(GA4_EVENT.DELETE_PRODUCT_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Dismiss',
      button_area: 'Page End',
      item_id: props.productId ?? '',
    });
  }
  closedByUsRef.value = true;
  closeDeleteModal();
};

const handleModalClosed = () => {
  if (props.pageName === "detail" && !closedByUsRef.value) {
    sendProductDetailEvent(GA4_EVENT.DELETE_PRODUCT_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Dismiss',
      button_area: 'Page End',
      item_id: props.productId ?? '',
    });
  }
  closedByUsRef.value = false;
};

const handleConfirmDelete = async () => {
  isDeleteLoading.value = true;
  try {
    const productIds = getProductIds();
    const payload = {
      productIds,
      ...(props.pageName === "listing" &&
        props.productStatus.includes("InApproval") && {
          tabParam: props.tabParam,
        }),
    };

    const res = await store.dispatch("products/deleteProducts", payload);
    const messageType = deleteMessageType(res.data);

    closedByUsRef.value = true;
    closeDeleteModal();

    if (props.pageName === "detail") {
      sendProductDetailEvent(GA4_EVENT.DELETE_PRODUCT_SUCCESS, {
        button_name: 'Ürünü Sil',
        button_type: 'Delete Product',
        button_area: 'Page End',
        item_id: props.productId ?? '',
      });
      await router.push({ name: "Products" });
      await new Promise((resolve) => setTimeout(resolve, 500));
      store.commit("common/SET_TOASTER_DATA", {
        type: ToastMessageTypes.SUCCESS,
        description: "Ürün silindi.",
      });
    } else {
      emit("successDeleteProduct");
      // GA4 event
      sendProductsDeletedEvent({ quantity: productIds.length });

      store.commit("common/SET_TOASTER_DATA", {
        type: messageType,
        description: res.data?.message || "Ürün silindi.",
      });
    }
    
  } catch (error) {
    console.error(error);
    store.commit("common/SET_TOASTER_DATA", {
      type: ToastMessageTypes.ERROR,
      title: ToastMessageTypes.ERROR_TITLE,
      description: ToastMessageTypes.ERROR_DELETE_DESCRIPTION,
    });
  } finally {
    isDeleteLoading.value = false;
  }
};

defineExpose({
  openDeleteModal,
});
</script>

<style lang="scss" scoped>
.delete-modal {
  &-list {
    list-style-type: disc;
    list-style-position: inside;
    font-weight: 600;
    text-align: center;
    line-height: 24px;
    letter-spacing: -0.05px;
    display: grid;
    gap: 6px;
  }

  &-bottomText {
    margin-bottom: 12px;
  }
}

.modal-text {
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.05px;
}
</style>